import React from 'react';
import {
  ButtonProps,
  CheckboxProps,
  Divider,
  Dropdown as AntdDropdown,
  DropdownProps,
  InputProps,
  MenuItemProps,
  Space
} from 'antd';
import {
  SearchOutlined,
  DownOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import styled from '@emotion/styled';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Button from '../Button';
import { useLazyLoadOption } from 'app/hooks/useLazyLoadOption';
import debounce from 'lodash/debounce';
import List from '../List';
import DropdownInspector from './Inspector';
import DropdownProductTags from './ProductTags';

const StyledAntdDropdown = styled(AntdDropdown)``;

const StyledContent = styled.div`
  padding: 8px;
  background: white;
  box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
    0 9px 28px 8px rgb(0 0 0 / 5%);
  li[role='menuitem'] {
    padding-left: 4px;
    background: transparent;
    &:hover {
      background: #ebf1ff;
    }
  }
  .ant-dropdown-menu {
    box-shadow: none;
  }
  .ant-divider {
    margin: 8px 0;
  }
`;
const StyledList = styled(List)`
  .infinite-scroll-component {
    max-height: 250px;
  }
  padding: 0px;
`;
const CheckboxItem = (
  props: Partial<
    MenuItemProps & {
      option: { value: string; label: string };
      checkboxProps: CheckboxProps;
    }
  >
) => {
  const { option, checkboxProps } = props;
  return <Checkbox {...checkboxProps}>{option?.label}</Checkbox>;
};

const Dropdown = (
  props: DropdownProps &
    Partial<{
      haveCheckbox?: boolean;
      onChangeSearch: (value: string) => void;
      showSearch: boolean;
      searchProps?: InputProps;
      footer: React.ReactNode;
    }>
) => {
  const { footer, showSearch, searchProps } = props;
  const [visible, setVisible] = React.useState(false);
  const handleVisibleChange = (open: boolean) => {
    setVisible(open);
  };

  return (
    <StyledAntdDropdown
      onOpenChange={handleVisibleChange}
      open={visible}
      dropdownRender={(menus) => {
        return (
          <StyledContent>
            {showSearch && (
              <>
                <Input
                  prefix={<SearchOutlined></SearchOutlined>}
                  {...searchProps}
                ></Input>
                <Divider></Divider>
              </>
            )}

            {menus}
            {footer && (
              <>
                <Divider></Divider>
                {footer}
              </>
            )}
          </StyledContent>
        );
      }}
      {...props}
    ></StyledAntdDropdown>
  );
};

const Footer = (props: {
  actions: Array<{
    key: string;
    label: React.ReactNode;
    buttonProps?: ButtonProps;
  }>;
}) => {
  const { actions, ...rest } = props;
  return (
    <Space {...rest}>
      {actions.map((action) => {
        return (
          <Button key={action.key} {...action.buttonProps}>
            {action.label}
          </Button>
        );
      })}
    </Space>
  );
};
const StyledFooter = styled(Footer)`
  margin-top: 8px;
  display: flex;
  padding-left: 0;
  gap: 8px;
`;

type lazyProps =
  | {
      lazy: false;
      lazyProps?: {
        fetch: (
          pagination: { currentPage: number; pageSize: number },
          search?: {
            keywords: string;
          },
          filter?: Record<string, any>
        ) => Awaited<
          | {
              value: string;
              label: string;
            }
          | { error?: string }
        >;
        loader?: React.ReactNode;
        transformOption?: (record: any) => {
          value: string;
          label: string;
        };
      };
    }
  | {
      lazy: true;
      lazyProps: {
        fetch: (
          pagination: { currentPage: number; pageSize: number },
          search?: {
            keywords: string;
          },
          filter?: Record<string, any>
        ) => Awaited<
          | Array<
              | {
                  value: string;
                  label: string;
                }
              | unknown[]
            >
          | { error?: string }
        >;
        loader?: React.ReactNode;
        transformOption: (record: any) => {
          value: string;
          label: string;
        };
      };
    }
  | {
      lazy?: boolean;
      lazyProps?: {
        fetch: (
          pagination: { currentPage: number; pageSize: number },
          search?: {
            keywords: string;
          },
          filter?: Record<string, any>
        ) => Awaited<
          | {
              value: string;
              label: string;
            }
          | { error?: string }
        >;
        loader?: React.ReactNode;
        transformOption?: (record: any) => {
          value: string;
          label: string;
        };
      };
    };

const FilterDropdown = ({
  footer,
  prefix,
  renderLabel,
  lazy,
  lazyProps,
  onApply,
  onReset,
  value = [],
  options = []
}: {
  options?: Array<{ value: string; label: string }>;
  value: Array<string | number>;
  onApply: (values: Array<string | number>) => void;
  onReset: () => void;
  renderLabel?: (config: {
    prefix: React.ReactNode;
    selections: Array<string>;
    options: Array<{ value: string; label: string }>;
    onClick: () => void;
  }) => React.ReactElement;
  footer?: React.ReactElement;
  prefix?: React.ReactNode;
  lazy?: boolean;
} & lazyProps) => {
  const [open, setOpen] = React.useState(false);
  const { onSearch, hasMore, loadMore, data, loading } = useLazyLoadOption({
    fetch: lazy ? (lazyProps?.fetch as any) : null,
    transformOption: (lazyProps as any)?.transformOption
  });
  const [keyword, setKeyword] = React.useState('');
  const [selectedValue, setSelectedValue] = React.useState<
    Array<string | number | any>
  >([]);

  const mappedOptions = React.useMemo(() => {
    return [...options.concat(data)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, data]);

  const onSelectOption = (option: any) => (event: any) => {
    const indexOf = selectedValue.indexOf(option.value);
    const nextSelectedValue = [...selectedValue];
    if (option.value === 'all') {
      setSelectedValue(
        event.target.checked ? mappedOptions.map(({ value }) => value) : []
      );
      return;
    }
    if (indexOf > -1) {
      nextSelectedValue.splice(indexOf, 1);
    } else {
      nextSelectedValue.push(option?.value);
    }
    setSelectedValue(nextSelectedValue.filter((value) => value !== 'all'));
  };
  const menuItems = React.useMemo(() => {
    const mapped = mappedOptions
      .filter(({ label }) => {
        if (lazy) {
          return true;
        }
        return keyword
          ? label.toLowerCase().includes(keyword.toLowerCase())
          : true;
      })
      .map(({ value, label }) => {
        const isChecked =
          selectedValue?.includes(value) || selectedValue?.includes('all');
        return {
          key: value,
          label: (
            <Dropdown.CheckBoxItem
              checkboxProps={{
                onChange: onSelectOption({ value, label }),
                checked: isChecked
              }}
              option={{
                value: value,
                label: label
              }}
            ></Dropdown.CheckBoxItem>
          )
        };
      });

    return mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedOptions, keyword, selectedValue]);

  const debounceFetch = debounce((searchKeywords: string) => {
    onSearch(searchKeywords);
  }, 250);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    if (lazy) {
      debounceFetch(event.target.value);
    }
  };

  const label = React.useMemo(() => {
    const selectedLabels = options
      .filter((option) => value?.includes(option.value))
      .map(({ label }) => label);
    return selectedLabels.join(',');
  }, [value, options]);

  const onClickApply = () => {
    if (onApply) {
      onApply(selectedValue);
    }
    setTimeout(() => {
      setOpen(false);
    });
  };

  const onClickReset = () => {
    let nextSelectedValue: Array<string | number> = [];
    if (mappedOptions.find(({ value }) => value === 'all')) {
      nextSelectedValue = mappedOptions.map(({ value }) => value);
      setSelectedValue(mappedOptions.map(({ value }) => value));
    }
    setSelectedValue(nextSelectedValue);
    onReset();
  };
  React.useEffect(() => {
    if (value.includes('all')) {
      setSelectedValue(['all'].concat(mappedOptions.map(({ value }) => value)));
    } else {
      setSelectedValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    if (!open) {
      if (value.includes('all')) {
        setSelectedValue(
          ['all'].concat(mappedOptions.map(({ value }) => value))
        );
      } else {
        setSelectedValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
      menu={{ items: menuItems }}
      dropdownRender={(menus) => {
        return (
          <StyledContent>
            <Input
              suffix={loading && <LoadingOutlined />}
              onChange={handleChange}
              prefix={<SearchOutlined></SearchOutlined>}
            ></Input>
            <Divider></Divider>
            <StyledList
              height={'auto'}
              isLazy={lazy}
              hasMore={hasMore}
              loadMoreData={() => {
                //TEMP: Using The Fake Function
                // We will using on Scroll to load more.
                // console.log('load more data');
                // loadMore();
              }}
              infiniteProps={{
                onScroll: (event: any) => {
                  if (
                    event?.target?.scrollTop + event?.target?.offsetHeight >=
                    event?.target?.scrollHeight - 50
                  ) {
                    loadMore();
                  }
                }
              }}
              dataSource={React.Children.toArray(menus)}
              renderItem={(menu) => {
                return menu;
              }}
            ></StyledList>
            <Divider></Divider>
            {footer ? (
              React.cloneElement(footer, {
                // selectedValue:
                onApply: onClickApply,
                onReset: onClickReset,
                options: mappedOptions,
                selectedValue
              })
            ) : (
              <>
                <Dropdown.Footer
                  actions={[
                    {
                      key: 'apply',
                      label: 'Apply',
                      buttonProps: {
                        type: 'primary',
                        onClick: onClickApply
                      }
                    },
                    {
                      key: 'reset',
                      label: 'Reset',
                      buttonProps: {
                        onClick: onClickReset
                      }
                    }
                  ]}
                ></Dropdown.Footer>
              </>
            )}
          </StyledContent>
        );
      }}
    >
      {renderLabel ? (
        renderLabel({
          prefix,
          selections: selectedValue as string[],
          options,
          onClick: () => {
            setOpen(true);
          }
        })
      ) : (
        <a
          onClick={() => {
            setOpen(true);
          }}
        >
          <Space size={8}>
            {prefix}
            {label}
            <DownOutlined />
          </Space>
        </a>
      )}
    </Dropdown>
  );
};

Dropdown.Footer = StyledFooter;
Dropdown.CheckBoxItem = CheckboxItem;
Dropdown.FilterDropdown = FilterDropdown;
Dropdown.Inspector = DropdownInspector;
Dropdown.CustomerTags = DropdownProductTags;

export default Dropdown;
